/*
 * Adapter contract: accepts snake_case or camelCase payloads and never throws.
 * Always return arrays instead of null, clamp 0-1 metrics, and keep a copy of
 * the original payload under `raw` for debugging. Any UI component that needs
 * sentiment data must go through this adapter.
 *
 * Also extracts sentiment_meta and computes pipelineStatus: "LIVE" | "STALE" | "OFFLINE"
 */

const STALE_THRESHOLD_SECONDS = 120; // Data older than 2min is considered stale

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const clamp01OrNull = (v) => {
  const n = toNum(v, null);
  return n === null ? null : Math.max(0, Math.min(1, n));
};

const arr = (v) => (Array.isArray(v) ? v : []);

const strList = (v) =>
  Array.isArray(v) ? v.filter((x) => typeof x === "string" && x.length > 0) : [];

// Shape returned when derivatives positioning is missing entirely. Kept as a
// named constant so both the "no payload" and "payload but zero live exchanges"
// paths resolve to an identical, provably-not-live object.
const UNAVAILABLE_POSITIONING = {
  available: false,
  status: "UNAVAILABLE",
  fundingBias: "unknown",
  averageFundingRate: null,
  configuredExchanges: [],
  liveExchanges: [],
  blockedExchanges: [],
  failedExchanges: [],
  coverageLive: 0,
  coverageTotal: 0,
  coverageRatio: 0,
  confidencePenalty: null,
  updatedAt: null,
  stale: false,
};

/**
 * Normalize the backend `social_metrics.derivatives_positioning` payload into a
 * flat, UI-friendly shape.
 *
 * Product rule enforced here: an exchange source must never be presented as
 * live unless the backend actually returned live data for it. `available` is
 * therefore keyed off `liveExchanges.length`, and `status` can only be "LIVE"
 * when at least one exchange is live and the payload is not stale.
 */
export function buildMarketPositioning(rawPositioning) {
  if (!rawPositioning || typeof rawPositioning !== "object") {
    return { ...UNAVAILABLE_POSITIONING };
  }

  const summary = pick(rawPositioning, "summary") || {};
  const liveExchanges = strList(
    pick(rawPositioning, "live_exchanges", "liveExchanges", "exchanges")
  );
  const blockedExchanges = strList(
    pick(rawPositioning, "blocked_exchanges", "blockedExchanges")
  );
  const failedExchanges = strList(
    pick(rawPositioning, "failed_exchanges", "failedExchanges")
  );
  const configuredExchanges = strList(
    pick(rawPositioning, "configured_exchanges", "configuredExchanges")
  );

  const available = liveExchanges.length > 0;
  const stale = Boolean(pick(rawPositioning, "stale"));
  const status = !available ? "UNAVAILABLE" : stale ? "STALE" : "LIVE";

  const coverageLive = toNum(
    pick(summary, "live_exchange_count", "liveExchangeCount"),
    liveExchanges.length
  );
  const coverageTotal = toNum(
    pick(summary, "configured_exchange_count", "configuredExchangeCount"),
    configuredExchanges.length
  );

  return {
    available,
    status,
    fundingBias: pick(summary, "funding_bias", "fundingBias") || "unknown",
    averageFundingRate: toNum(
      pick(summary, "average_funding_rate", "averageFundingRate"),
      null
    ),
    configuredExchanges,
    liveExchanges,
    blockedExchanges,
    failedExchanges,
    coverageLive,
    coverageTotal,
    coverageRatio: toNum(
      pick(summary, "coverage_ratio", "coverageRatio"),
      coverageTotal ? coverageLive / coverageTotal : 0
    ),
    confidencePenalty: toNum(
      pick(summary, "confidence_penalty", "confidencePenalty"),
      null
    ),
    updatedAt: pick(rawPositioning, "updated_at", "updatedAt", "ts") || null,
    stale,
  };
}

/**
 * Compute pipeline status from sentiment_meta
 * @param {object|null} meta - sentiment_meta from backend
 * @param {boolean} hasData - whether we have any sentiment data
 * @returns {"LIVE" | "STALE" | "OFFLINE"}
 */
const computePipelineStatus = (meta, hasData) => {
  if (!meta) return hasData ? "STALE" : "OFFLINE";
  const staleSeconds = toNum(meta.staleSeconds, Infinity);
  const dataStatus = String(meta.dataStatus || meta.data_status || "").toLowerCase();
  if (
    meta.ok &&
    meta.pipelineRunning &&
    dataStatus !== "stale" &&
    staleSeconds < STALE_THRESHOLD_SECONDS
  ) return "LIVE";
  if (hasData) return "STALE";
  return "OFFLINE";
};

export function normalizeSentiment(raw = {}) {
  // accept both snake_case and camelCase
  const overallSentiment = clamp01OrNull(
    pick(raw, "overall_sentiment", "overallSentiment")
  );

  const fgBlock = pick(raw, "fear_greed", "fearGreed") || {};
  const fgValue = toNum(pick(fgBlock, "value", "index", "score", "fear_greed_index"), null);
  const fearGreedIndex = fgValue !== null
    ? fgValue
    : toNum(pick(raw, "fear_greed_index", "fearGreedIndex"), null);
  const fearGreedLabel =
    pick(fgBlock, "label", "classification", "status", "value_classification") ||
    pick(raw, "fear_greed_label", "fearGreedLabel") ||
    null;
  const fearGreedUpdatedAt =
    pick(fgBlock, "updated_at", "timestamp", "ts") ||
    pick(raw, "fear_greed_timestamp", "fearGreedTimestamp") ||
    pick(raw, "updated_at", "updatedAt") ||
    null;
  const fearGreedStatus =
    fearGreedIndex !== null
      ? (fgBlock?.stale ? "STALE" : "LIVE")
      : "UNAVAILABLE";

  const marketPulseRaw = pick(raw, "market_pulse", "marketPulse", "market") || {};
  const marketPulse = {
    totalMarketCap: toNum(pick(marketPulseRaw, "total_market_cap_usd", "market_cap_usd", "total_market_cap"), null),
    totalVolume: toNum(pick(marketPulseRaw, "total_volume_usd", "volume_usd", "total_volume"), null),
    btcDominance: toNum(pick(marketPulseRaw, "btc_dominance", "btc_dominance_pct", "btc_dominance_usd"), null),
    mcapChangePct: toNum(
      pick(
        marketPulseRaw,
        "mcap_change_24h_pct",
        "mcap_change_pct"
      ),
      null
    ),
    updatedAt: pick(marketPulseRaw, "updated_at", "timestamp", "ts") || null,
    sourceUrl: pick(marketPulseRaw, "source_url", "sourceUrl") || null,
    stale: Boolean(pick(marketPulseRaw, "stale")),
  };
  const marketPulseStatus =
    marketPulse.totalMarketCap !== null || marketPulse.totalVolume !== null
      ? marketPulse.stale
        ? "STALE"
        : "LIVE"
      : "UNAVAILABLE";

  const socialMetricsRaw = pick(raw, "social_metrics", "socialMetrics") || {};
  const socialMetrics = {
    volumeChange: toNum(pick(socialMetricsRaw, "volume_change", "volumeChange"), null),
    engagementRate: clamp01OrNull(pick(socialMetricsRaw, "engagement_rate", "engagementRate")),
    mentions: toNum(
      pick(socialMetricsRaw, "mentions", "mentions_1h", "mentions1h", "mentions_24h"),
      null,
    ),
  };

  // Derivatives / market-positioning health lives inside social_metrics on the
  // backend response. Surface it here so every consumer of this adapter gets a
  // consistent, provenance-aware view instead of reaching into `raw`.
  const marketPositioning = buildMarketPositioning(
    pick(socialMetricsRaw, "derivatives_positioning", "derivativesPositioning")
  );

  const socialBreakdownRaw =
    pick(raw, "social_breakdown", "socialBreakdown") || {};
  const socialBreakdown = {
    reddit: clamp01OrNull(pick(socialBreakdownRaw, "reddit")),
    twitter: clamp01OrNull(pick(socialBreakdownRaw, "twitter")),
    telegram: clamp01OrNull(pick(socialBreakdownRaw, "telegram")),
    stocktwits: clamp01OrNull(pick(socialBreakdownRaw, "stocktwits")),
    chan: clamp01OrNull(pick(socialBreakdownRaw, "chan")),
    custom: clamp01OrNull(pick(socialBreakdownRaw, "custom")),
  };

  const sourceBreakdownRaw =
    pick(raw, "source_breakdown", "sourceBreakdown") || {};
  const sourceBreakdown = {
    tier1: toNum(pick(sourceBreakdownRaw, "tier1"), 0),
    tier2: toNum(pick(sourceBreakdownRaw, "tier2"), 0),
    tier3: toNum(pick(sourceBreakdownRaw, "tier3"), 0),
    fringe: toNum(pick(sourceBreakdownRaw, "fringe"), 0),
  };

  const sentimentHistoryRaw = arr(
    pick(raw, "sentiment_history", "sentimentHistory")
  );
  const sentimentHistory = sentimentHistoryRaw.map((p) => ({
    timestamp: pick(p, "timestamp"),
    sentiment: clamp01OrNull(pick(p, "sentiment")),
    priceNormalized: toNum(pick(p, "price_normalized", "priceNormalized"), null),
  }));

  const socialHistoryRaw = arr(pick(raw, "social_history", "socialHistory"));
  const socialHistory = socialHistoryRaw.map((p) => ({
    timestamp: pick(p, "timestamp"),
    reddit: clamp01OrNull(pick(p, "reddit")),
    twitter: clamp01OrNull(pick(p, "twitter")),
    telegram: clamp01OrNull(pick(p, "telegram")),
    chan: clamp01OrNull(pick(p, "chan")),
    custom: clamp01OrNull(pick(p, "custom")),
  }));

  const trendingTopicsRaw = arr(
    pick(raw, "trending_topics", "trendingTopics")
  );
  const trendingTopics = trendingTopicsRaw.map((t) => ({
    tag: pick(t, "tag") || "",
    sentiment: pick(t, "sentiment") || "neutral",
    volume: pick(t, "volume") || "",
  }));

  const divergenceAlertsRaw = arr(
    pick(raw, "divergence_alerts", "divergenceAlerts")
  );
  const divergenceAlerts = divergenceAlertsRaw.map((a) => ({
    type: pick(a, "type") || "info",
    message: pick(a, "message") || "",
  }));

  // Tiered sentiment data (optional, from sentiment pipeline)
  const tierScoresRaw = pick(raw, "tier_scores", "tierScores") || {};
  const tierScores = tierScoresRaw ? {
    tier1: clamp01OrNull(pick(tierScoresRaw, "tier1")),
    tier2: clamp01OrNull(pick(tierScoresRaw, "tier2")),
    tier3: clamp01OrNull(pick(tierScoresRaw, "tier3")),
    fringe: clamp01OrNull(pick(tierScoresRaw, "fringe")),
  } : null;

  const hasTieredData = pick(raw, "has_tiered_data", "hasTieredData") || false;
  const totalDataPoints = toNum(pick(raw, "total_data_points", "totalDataPoints"), 0);
  const confidence = clamp01OrNull(pick(raw, "confidence"));
  const pipelineTimestamp = pick(raw, "pipeline_timestamp", "pipelineTimestamp") || null;

  const updatedAt = pick(raw, "updated_at", "updatedAt", "ts", "timestamp") || null;

  // Extract sentiment_meta from backend response
  const sentimentMeta = pick(raw, "sentiment_meta", "sentimentMeta") || null;
  const hasData = overallSentiment !== null || fearGreedIndex !== null;
  const pipelineStatus = computePipelineStatus(sentimentMeta, hasData);

  return {
    overallSentiment,
    fearGreedIndex,
    socialMetrics,
    marketPositioning,
    socialBreakdown,
    sourceBreakdown,
    sentimentHistory,
    socialHistory,
    trendingTopics,
    divergenceAlerts,
    // Tiered data fields
    tierScores,
    hasTieredData,
    totalDataPoints,
    confidence,
    pipelineTimestamp,
    updatedAt,
    fearGreedLabel,
    fearGreedUpdatedAt,
    fearGreedStatus,
    marketPulse,
    marketPulseStatus,
    // Pipeline truth-state contract
    sentimentMeta,
    pipelineStatus, // "LIVE" | "STALE" | "OFFLINE"
    raw,
  };
}

export default normalizeSentiment;
